import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type {
  ApplicationPlanType,
  EmployeeMassActionResultDto,
  LegalParameterSetDto,
  Unit,
  UpdateLegalParameterSetRequest,
} from '@flexyoo/api/models';
import { AdminApiService, EmployeeApiService, TenantApiService } from '@flexyoo/api/services';
import { environment } from '@flexyoo/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IPayrollSetting } from '../../../../shared/models/tenants/tenant-payroll-setting.inteface';

@Injectable({ providedIn: 'root' })
export class AdminSettingsDataAccessService {
  private readonly http = inject(HttpClient);
  private readonly adminApi = inject(AdminApiService);
  private readonly employeeApi = inject(EmployeeApiService);
  private readonly tenantApi = inject(TenantApiService);

  public getLegalParameters(year: number): Observable<LegalParameterSetDto> {
    return this.adminApi.getLegalParameterSet({ year });
  }

  public getOnssImportDate(): Observable<string> {
    return this.adminApi.onssImportDate();
  }

  public updateLegalParameters(parameters: UpdateLegalParameterSetRequest): Observable<Unit> {
    return this.adminApi.createLegalParameterSet(parameters);
  }

  downloadPrivacyStatement(language: string) {
    return this.http
      .get(`${environment.static}/policy/privacy-statement-${language}.pdf`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((result: any) => {
          return result;
        })
      );
  }

  exportONSSFile(): Observable<Blob> {
    return this.adminApi.onssExport().pipe(map((response) => response.body));
  }

  importONSSFile(formData: FormData): Observable<string> {
    return this.adminApi.onssImport(formData);
  }

  public getTenantDayToSendPayroll(tenantId: number): Observable<IPayrollSetting> {
    return this.tenantApi.getPayrollSettings(tenantId).pipe(
      map((result) => ({
        Id: result.Id,
        DayToSendPayroll: result.DayToSendPayroll,
        SendPayrollOnLastMonthDay: result.SendPayrollOnLastMonthDay,
      }))
    );
  }

  public updateTenantDaySendingPayroll(
    tenantId: number,
    payrollSetting: IPayrollSetting
  ): Observable<IPayrollSetting> {
    return this.tenantApi
      .updatePayrollSettings(tenantId, {
        SendPayrollOnLastMonthDay: payrollSetting.SendPayrollOnLastMonthDay,
        DayToSendPayroll: payrollSetting.DayToSendPayroll ?? undefined,
      })
      .pipe(
        map((result) => ({
          Id: result.Id,
          DayToSendPayroll: result.DayToSendPayroll,
          SendPayrollOnLastMonthDay: result.SendPayrollOnLastMonthDay,
        }))
      );
  }

  public activateEmails(
    dateTo: string,
    employeeIds: number[],
    applicationPlanTypes: ApplicationPlanType[]
  ): Observable<EmployeeMassActionResultDto> {
    return this.employeeApi.activateEmail({
      EmployeeIds: employeeIds,
      UserAgreementSignEndDate: dateTo,
      ApplicationPlanTypes: applicationPlanTypes,
    });
  }
}
